// import packages
import { Caption, Card, Layout, Link, PageActions, RadioButton, Stack, TextContainer, TextStyle } from "@shopify/polaris";
import { useMutation, useQuery } from "@apollo/react-hooks";
import React, { useContext, useEffect, useState } from "react";

// import context
import { PrivateContext } from "lib/context";

// import hoc
import { withErrorBoundary, withFeature } from "lib/hoc";

// import components
import { Banner, SkeletonAnnotated, Toast } from "lib/components";

// import helpers
import { baseHelper, errorHelper } from "lib/helpers";

// import cms and constants
import constant from "lib/constant/constant";
import GET_PRODUCT_SETTING from "app/setup/apollo/queries/getProductSetting";
import { GET_VENDOR_AND_PRODUCT_TYPES } from "app/setup/apollo";

// import gql
import { UPDATE_SETTING, UPDATE_ADVANCE_PRODUCT_APPROVAL_SETTING } from "app/productOld/apollo/mutations";
import fieldData from "app/productOld/modules/operator/features/approvalSetting/cms/fielfData";

// import autopublish
import AutoPublish from "app/productOld/modules/operator/features/approvalSetting/subFeature/autoPublish";

// import advance approval components
import AdvanceApprovalTable from "./components/advanceApproval/table";
import AddVendorApproval from "./components/advanceApproval/addVendor";

const { APPROVE_ALL, APPROVE_SELECTED, gql, REVIEW_ALL } = constant;
const OperatorApprovalSetting = () => {
  const [buttonValue, setButtonValue] = useState(true);
  const { data: productSettingsData, loading, refetch } = useQuery(GET_PRODUCT_SETTING);
  const { data: vendorTypesData, loading: vendorListLoading } = useQuery(GET_VENDOR_AND_PRODUCT_TYPES);
  const { cms, history } = useContext(PrivateContext);
  const [isHideTag, setIsHideTag] = useState(false);
  const [isHideType, setIsHideType] = useState(false);
  const [isHideTagAndType, setIsHideTagAndType] = useState(false);
  const [isCatalogHidden, setIsCatalogHidden] = useState(true);
  const [lastUpdated, SetLastUpdated] = useState("");
  const [message, setMessage] = useState("");
  const [publish, setPublish] = useState("");
  const productSettingData = fieldData(cms);
  const [radioButtonValue, setRadioButtonValue] = useState(false);
  const [radioButton, setRadioButton] = useState(false);
  const [rate, setRate] = useState("");
  const [selected, setSelected] = useState("");
  const [updateProductSetting, { loading: productSettingLoading }] = useMutation(UPDATE_SETTING);
  const [updateAdvanceProductApprovalSetting, { loading: advanceApprovalLoading }] = useMutation(UPDATE_ADVANCE_PRODUCT_APPROVAL_SETTING);
  const [bannerStatus, setBannerStatus] = useState({
    title: "",
    status: "",
    isOpen: false,
  });

  // Advance product approval state
  const [advanceApprovalInput, setAdvanceApprovalInput] = useState([]);
  const [vendorList, setVendorList] = useState([]);
  const [advanceApprovalModalActive, setAdvanceApprovalModalActive] = useState(false);
  const [advanceApprovalDisabled, setAdvanceApprovalDisabled] = useState(true);
  const [advanceApprovalMessage, setAdvanceApprovalMessage] = useState("");

  const handleChange = (checked, newValue) => {
    setRadioButtonValue(newValue);
    setButtonValue(false);
  };

  const onSubmit = () => {
    setButtonValue(true);
    updateProductSetting({
      variables: {
        input: {
          reviewAll: radioButtonValue === REVIEW_ALL,
          approveSelected: radioButtonValue === APPROVE_SELECTED,
          approveAll: radioButtonValue === APPROVE_ALL,
        },
      },
    })
      .then((res) => {
        const responseData = baseHelper.getResponseData(res.data, constant.gql.UPDATE_PRODUCT_SETTING);
        if (!responseData) {
          const errorResponse = baseHelper.getResponseError(res.data, constant.gql.UPDATE_PRODUCT_SETTING);
          setBannerStatus({
            status: constant.CRITICAL,
            title: errorResponse,
            isOpen: true,
          });
        }
        setMessage(cms("message.success.approval"));
      })
      .catch((exception) => {
        setBannerStatus({
          status: constant.CRITICAL,
          title: errorHelper.parse(exception),
          isOpen: true,
        });
      });
  };

  const renderBanner = () => {
    const { status, title, isOpen } = bannerStatus;
    return (
      <Banner
        isOpen={isOpen}
        status={status}
        title={title}
        onDismiss={() => setBannerStatus({ isOpen: false, status: "", title: "" })}
      />
    );
  };
  useEffect(() => {
    const responseData = baseHelper.getResponseData(productSettingsData, gql.GET_PRODUCT_SETTING);
    if (!responseData) {
      return;
    }
    const {
      advanceProductApproval = [],
      productApproval = "",
      updatedAt = "",
      isPriceTaxInclusive = false,
      rate: amount,
      isHideTag: hideTag,
      isHideTagAndType: hideTagAndType,
      isHideType: hideType,
      isAutoPublish = false,
      isCatalogHidden: catalogHidden,
    } = responseData;
    setPublish(isAutoPublish);
    setRadioButtonValue(productApproval);
    setRadioButton(productApproval);
    SetLastUpdated(updatedAt);
    setIsHideTag(hideTag);
    setIsHideTagAndType(hideTagAndType);
    setIsHideType(hideType);
    setIsCatalogHidden(catalogHidden);
    setAdvanceApprovalInput(advanceProductApproval || []);
    if (!isPriceTaxInclusive) {
      setSelected("false");
      return;
    }
    setSelected("true");
    setRate(amount || 0);
  }, [productSettingsData]);

  useEffect(() => {
    const responseData = baseHelper.getResponseData(vendorTypesData, gql.GET_VENDOR_PRODUCT);
    if (!responseData) {
      return;
    }
    const { supplierRows = [] } = responseData;
    setVendorList(supplierRows);
  }, [vendorTypesData]);

  const onSubmitAdvanceApproval = () => {
    updateAdvanceProductApprovalSetting({
      variables: {
        input: {
          advanceProductApproval: advanceApprovalInput.map(({ vendorId, productApproval }) => ({
            vendorId,
            productApproval,
          })),
        },
      },
    })
      .then((res) => {
        const responseData = baseHelper.getResponseData(res.data, gql.UPDATE_ADVANCE_PRODUCT_APPROVAL_SETTING);
        if (!responseData) {
          const errorResponse = baseHelper.getResponseError(res.data, gql.UPDATE_ADVANCE_PRODUCT_APPROVAL_SETTING);
          setBannerStatus({ status: constant.CRITICAL, title: errorResponse, isOpen: true });
          return;
        }
        setAdvanceApprovalMessage("Advanced product approval settings saved.");
        setAdvanceApprovalDisabled(true);
        refetch();
      })
      .catch((exception) => {
        setBannerStatus({ status: constant.CRITICAL, title: errorHelper.parse(exception), isOpen: true });
      });
  };

  if (loading || vendorListLoading) {
    return <SkeletonAnnotated />;
  }

  const globalApprovalLabel = (productSettingData.find((item) => item.key === radioButtonValue) || {}).label || radioButtonValue;

  return (
    <>
      {bannerStatus.isOpen && <Layout.Section>{renderBanner()}</Layout.Section>}
      <Layout.AnnotatedSection title="Global Product Approval" description={cms("section.approval.description")}>
        <Card
          title={[
            "Global Product Approval",
            <TextStyle variation="subdued">
              {lastUpdated && <Caption>{`${baseHelper.lastUpdateDate(lastUpdated)}`}</Caption>}
            </TextStyle>,
          ]}
        >
          <Card.Section>
            <Stack vertical>
              <TextContainer>{cms("section.approval.description")}</TextContainer>
              {productSettingData.map((item) => {
                const { label: labelItem, helpText, key, name } = item;
                return (
                  <RadioButton
                    id={key}
                    key={key}
                    label={labelItem}
                    helpText={helpText}
                    name={name}
                    checked={radioButtonValue === key}
                    onChange={handleChange}
                  />
                );
              })}
            </Stack>
            <div className="manageVendorAccessButton">
              <PageActions
                primaryAction={{
                  id: "submitProductSetting",
                  content: cms("common.button.update"),
                  disabled: productSettingLoading || buttonValue,
                  loading: productSettingLoading,
                  onAction: () => onSubmit(),
                }}
                secondaryActions={[
                  {
                    id: "cancelProductSetting",
                    content: cms("common.button.cancel"),
                    onAction: () => history.push("/setting"),
                  },
                ]}
              />
            </div>
          </Card.Section>
        </Card>
        <div className="toast">
          <Toast message={message} setToast={setMessage} />
        </div>
      </Layout.AnnotatedSection>
      <br />
      <Layout.AnnotatedSection
        title="Advanced Product Approval"
        description="Override the global product approval setting for specific vendors. Vendors without an override will fall back to the global setting above."
      >
        <Card title="Advanced Product Approval">
          <Card.Section>
            <TextContainer>
              <p>
                Set a vendor-specific product approval mode. If no override is set for a vendor, the global setting (
                <TextStyle variation="strong">{globalApprovalLabel}</TextStyle>) applies.
              </p>
            </TextContainer>
            <br />
            <AdvanceApprovalTable
              userInput={advanceApprovalInput}
              setUserInput={setAdvanceApprovalInput}
              vendorList={vendorList}
              setDisable={setAdvanceApprovalDisabled}
            />
            <br />
            <Link onClick={() => setAdvanceApprovalModalActive(true)}>
              + Add vendor override
            </Link>
            {advanceApprovalModalActive && (
              <AddVendorApproval
                active={advanceApprovalModalActive}
                setActive={setAdvanceApprovalModalActive}
                vendorList={vendorList}
                setVendorList={setVendorList}
                userInput={advanceApprovalInput}
                setUserInput={setAdvanceApprovalInput}
                setDisable={setAdvanceApprovalDisabled}
                cms={cms}
              />
            )}
            <div className="manageVendorAccessButton">
              <PageActions
                primaryAction={{
                  id: "submitAdvanceApproval",
                  content: "Save Advanced Settings",
                  disabled: advanceApprovalLoading || advanceApprovalDisabled,
                  loading: advanceApprovalLoading,
                  onAction: () => onSubmitAdvanceApproval(),
                }}
                secondaryActions={[
                  {
                    id: "cancelAdvanceApproval",
                    content: cms("common.button.cancel"),
                    onAction: () => history.push("/setting"),
                  },
                ]}
              />
            </div>
          </Card.Section>
        </Card>
        <div className="toast">
          <Toast message={advanceApprovalMessage} setToast={setAdvanceApprovalMessage} />
        </div>
      </Layout.AnnotatedSection>
      <br />
      <AutoPublish
        lastUpdated={lastUpdated}
        selected={selected}
        rate={rate}
        radioButtonValue={radioButton}
        isHideTagAndType={isHideTagAndType}
        setIsHideTagAndType={setIsHideTagAndType}
        setBannerStatus={setBannerStatus}
        refetch={refetch}
        isAutoPublish={publish}
        setIsAutoPublish={setPublish}
      />
    </>
  );
};

export default withFeature(withErrorBoundary(OperatorApprovalSetting), {
  feature: constant.PRODUCT_APPROVAL_SETTING,
});
